/* GrowthOS — Leads filter panel (ported from the claude.ai/design LeadsList).
   The design filters client-side; this app filters server-side through AG Grid,
   so the panel edits a `conds` model and we translate it to an AG Grid
   filterModel (see buildAgFilterModel) which the existing onFilterChanged →
   filterJson → fetch pipeline already understands. */
import { useEffect, useRef, useState } from 'react';

const ICONS = {
  filter: 'M3 5h18l-7 8v6l-4-2v-4L3 5Z',
  search: 'M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16ZM21 21l-4.3-4.3',
  trend: 'M3 17l6-6 4 4 8-8M21 7v5h-5',
  calendar: 'M8 2v4M16 2v4M3 9h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z',
  check: 'M20 6 9 17l-5-5',
  chevD: 'M6 9l6 6 6-6',
  x: 'M18 6 6 18M6 6l12 12',
};

const FIcon = ({ name, size = 15, className }) => (
  <svg className={className} viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <path d={name === 'check' && size <= 12 ? ICONS.check : ICONS[name]} />
  </svg>
);

const TYPE_ICON = { set: 'filter', text: 'search', number: 'trend', date: 'calendar', boolean: 'check' };
const TEXT_OPS = [['contains', 'contains'], ['equals', 'equals'], ['startsWith', 'starts with']];
const NUM_OPS = [['between', 'between'], ['gt', 'greater than'], ['lt', 'less than'], ['eq', 'equals']];

// ---- field model derived from the app's dropdown data ----
export const makeFilterFields = (o = {}) => [
  { field: 'owner', label: 'Lead Owner', type: 'set', vals: (o.owners || []).map((x) => x.display_name).filter(Boolean) },
  { field: 'leadSource', label: 'Lead Source', type: 'set', vals: (o.leadSources || []).map((x) => x.displayName).filter(Boolean) },
  { field: 'industry', label: 'Industry', type: 'set', vals: (o.industries || []).map((x) => x.displayName).filter(Boolean) },
  { field: 'campaignId', label: 'Campaign', type: 'set', vals: (o.campaigns || []).map((x) => x.campaignName).filter(Boolean) },
  { field: 'revenueType', label: 'Revenue Type', type: 'set', vals: (o.revenueTypes || []).map((x) => x.displayName).filter(Boolean) },
  { field: 'timeZone', label: 'Time Zone', type: 'set', vals: (o.timeZones || []).map((x) => x.displayName).filter(Boolean) },
  { field: 'companyName', label: 'Company', type: 'text' },
  { field: 'email', label: 'Email', type: 'text' },
  { field: 'title', label: 'Title', type: 'text' },
  { field: 'annualRevenue', label: 'Annual Revenue', type: 'number', prefix: '$' },
  { field: 'noOfEmployees', label: 'No. of Employees', type: 'number' },
  { field: 'createdDateTime', label: 'Created Date', type: 'date' },
  { field: 'emailOptOut', label: 'Email Opt Out', type: 'boolean' },
];

export const defaultCond = (type) => {
  if (type === 'set') return { type, values: [] };
  if (type === 'text') return { type, op: 'contains', value: '' };
  if (type === 'number') return { type, op: 'between', v1: '', v2: '' };
  if (type === 'date') return { type, from: '', to: '' };
  if (type === 'boolean') return { type, value: true };
  return { type };
};

export const condActive = (c) => {
  if (!c) return false;
  if (c.type === 'set') return c.values && c.values.length > 0;
  if (c.type === 'text') return !!(c.value && c.value.trim());
  if (c.type === 'number') return (c.v1 !== '' && c.v1 != null) || (c.op === 'between' && c.v2 !== '' && c.v2 != null);
  if (c.type === 'date') return !!(c.from || c.to);
  if (c.type === 'boolean') return c.value === true || c.value === false;
  return false;
};

export const summarize = (c, meta) => {
  if (!c) return '';
  if (c.type === 'set') return c.values.length <= 2 ? c.values.join(', ') : c.values.length + ' selected';
  if (c.type === 'text') return ({ contains: 'contains', equals: 'is', startsWith: 'starts' })[c.op] + ' "' + c.value + '"';
  if (c.type === 'number') {
    const p = (meta && meta.prefix) || '';
    if (c.op === 'between') return p + (c.v1 || '0') + ' – ' + p + (c.v2 || '∞');
    return ({ gt: '> ', lt: '< ', eq: '= ' })[c.op] + p + c.v1;
  }
  if (c.type === 'date') return (c.from || '…') + ' → ' + (c.to || '…');
  if (c.type === 'boolean') return c.value ? 'Yes' : 'No';
  return '';
};

const num = (v) => (v === '' || v == null ? null : Number(v));

// translate the conds model → AG Grid filterModel (server pipeline handles the rest)
export const buildAgFilterModel = (conds, fields) => {
  const model = {};
  Object.entries(conds).forEach(([field, c]) => {
    if (!condActive(c)) return;
    if (c.type === 'set') model[field] = { filterType: 'set', values: c.values };
    else if (c.type === 'text') {
      const type = c.op === 'equals' ? 'equals' : c.op === 'startsWith' ? 'startsWith' : 'contains';
      model[field] = { filterType: 'text', type, filter: c.value.trim() };
    } else if (c.type === 'number') {
      if (c.op === 'between') model[field] = { filterType: 'number', type: 'inRange', filter: num(c.v1), filterTo: num(c.v2) };
      else model[field] = { filterType: 'number', type: c.op === 'gt' ? 'greaterThan' : c.op === 'lt' ? 'lessThan' : 'equals', filter: num(c.v1) };
    } else if (c.type === 'date') {
      model[field] = { filterType: 'date', type: 'inRange', dateFrom: c.from || null, dateTo: c.to || null };
    } else if (c.type === 'boolean') {
      model[field] = { filterType: 'set', values: [c.value] };
    }
  });
  return model;
};

// ---- the active-filter chips bar (rendered below the toolbar) ----
export const ActiveFilterBar = ({ fields, conds, onEdit, onRemove, onClear }) => {
  const active = Object.entries(conds).filter(([, c]) => condActive(c));
  if (active.length === 0) return null;
  const meta = (f) => fields.find((x) => x.field === f);
  return (
    <div className="filter-bar">
      <span className="filter-bar-label">Filtered by</span>
      {active.map(([field, c]) => (
        <button key={field} type="button" className="active-chip" onClick={() => onEdit(field)}>
          <span className="ac-key">{meta(field)?.label}:</span>
          {summarize(c, meta(field))}
          <span
            className="ac-x"
            onClick={(e) => {
              e.stopPropagation();
              onRemove(field);
            }}
          >
            <FIcon name="x" size={12} />
          </span>
        </button>
      ))}
      <button type="button" className="link" style={{ fontSize: 12 }} onClick={onClear}>
        Clear all
      </button>
    </div>
  );
};

// ---- the Filters button + dropdown panel ----
const LeadsFilters = ({ fields, conds, setCond, removeCond, clearAll, onApply, expanded, setExpanded, open, setOpen }) => {
  const [fieldQuery, setFieldQuery] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const filterCount = Object.values(conds).filter(condActive).length;
  const shown = fields.filter((m) => m.label.toLowerCase().includes(fieldQuery.trim().toLowerCase()));

  const editorFor = (m) => {
    const c = conds[m.field] || defaultCond(m.type);
    const fld = m.field;
    if (m.type === 'set') {
      return (
        <div className="filter-opts">
          {m.vals.map((v) => {
            const on = (c.values || []).includes(v);
            return (
              <button
                key={v}
                type="button"
                className={'filter-chip' + (on ? ' on' : '')}
                onClick={() => setCond(fld, { values: on ? c.values.filter((x) => x !== v) : [...(c.values || []), v] })}
              >
                {on ? <FIcon name="check" size={12} /> : null}
                {v}
              </button>
            );
          })}
          {m.vals.length === 0 ? <span className="filter-empty">No values</span> : null}
        </div>
      );
    }
    if (m.type === 'text') {
      return (
        <div className="filter-row">
          <select className="filter-input filter-op" value={c.op} onChange={(e) => setCond(fld, { op: e.target.value })}>
            {TEXT_OPS.map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </select>
          <input className="filter-input" placeholder="Value…" value={c.value} onChange={(e) => setCond(fld, { value: e.target.value })} />
        </div>
      );
    }
    if (m.type === 'number') {
      return (
        <div className="filter-row col">
          <select className="filter-input filter-op" value={c.op} onChange={(e) => setCond(fld, { op: e.target.value })}>
            {NUM_OPS.map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </select>
          <div className="filter-row">
            <input className="filter-input" type="number" placeholder={m.prefix ? m.prefix + 'min' : 'value'} value={c.v1} onChange={(e) => setCond(fld, { v1: e.target.value })} />
            {c.op === 'between' ? (
              <>
                <span className="filter-and">and</span>
                <input className="filter-input" type="number" placeholder={m.prefix ? m.prefix + 'max' : 'value'} value={c.v2} onChange={(e) => setCond(fld, { v2: e.target.value })} />
              </>
            ) : null}
          </div>
        </div>
      );
    }
    if (m.type === 'date') {
      return (
        <div className="filter-row">
          <input className="filter-input" type="date" value={c.from} onChange={(e) => setCond(fld, { from: e.target.value })} />
          <span className="filter-and">→</span>
          <input className="filter-input" type="date" value={c.to} onChange={(e) => setCond(fld, { to: e.target.value })} />
        </div>
      );
    }
    if (m.type === 'boolean') {
      return (
        <div className="seg seg-bool">
          {[[true, 'Yes'], [false, 'No']].map(([val, lbl]) => (
            <button key={lbl} type="button" className={c.value === val ? 'on' : ''} onClick={() => setCond(fld, { value: val })}>
              {lbl}
            </button>
          ))}
        </div>
      );
    }
    return null;
  };

  const done = () => {
    onApply(conds);
    setOpen(false);
  };

  return (
    <div className="gos-filter-wrap" ref={ref}>
      <button
        type="button"
        className={'gos-btn gos-btn-ghost gos-filter-btn' + (open ? ' on' : '') + (filterCount ? ' has-count' : '')}
        onClick={() => setOpen((o) => !o)}
      >
        <FIcon name="filter" size={15} />
        Filters
        {filterCount ? <span className="count">{filterCount}</span> : null}
      </button>
      {open && (
        <div className="dropdown gos-filter-menu">
          <div className="filter-panel">
            <div className="filter-search">
              <FIcon name="search" size={15} />
              <input placeholder="Find a field to filter…" value={fieldQuery} onChange={(e) => setFieldQuery(e.target.value)} />
            </div>
            <div className="filter-list">
              {shown.length === 0 ? <div className="filter-empty">No fields match.</div> : null}
              {shown.map((m) => {
                const active = condActive(conds[m.field]);
                const isOpen = expanded === m.field;
                return (
                  <div key={m.field} className={'filter-field' + (isOpen ? ' open' : '') + (active ? ' active' : '')}>
                    <button type="button" className="filter-field-head" onClick={() => setExpanded(isOpen ? null : m.field)}>
                      <FIcon name={TYPE_ICON[m.type] || 'filter'} size={14} className="ff-type" />
                      <span className="ff-label">{m.label}</span>
                      {active ? <span className="ff-summary">{summarize(conds[m.field], m)}</span> : null}
                      <FIcon name="chevD" size={14} className="ff-caret" />
                    </button>
                    {isOpen ? (
                      <div className="filter-field-body">
                        {editorFor(m)}
                        {active ? (
                          <button type="button" className="filter-clear-mini" onClick={() => removeCond(m.field)}>
                            Clear this filter
                          </button>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
            <div className="filter-foot">
              <span className="faint" style={{ fontSize: 12 }}>{filterCount ? filterCount + ' active' : 'No filters'}</span>
              <div className="gos-foot-actions">
                <button type="button" className="link" disabled={!filterCount} onClick={clearAll} style={{ opacity: filterCount ? 1 : 0.4 }}>
                  Clear all
                </button>
                <button type="button" className="gos-btn gos-btn-primary gos-btn-sm" onClick={done}>
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadsFilters;
